import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ChannelType } from '../../../types/signal';
import { ingestSignal } from '../../../utils/api';
import { ThreadMessage } from '../../../types/threadMessage';
import DatasetSelector from '../shared/DatasetSelector';
import { useQueryClient } from '@tanstack/react-query';
import { datasetKeys } from '../../../hooks/useDatasets';

interface SignalCreationFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (response: any) => void;
  onBack?: () => void;
}

// Using ThreadMessage type from centralized definition
// Legacy interface removed - using imported ThreadMessage from types/threadMessage.ts

interface SignalFormData {
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_id?: string;
  verbatim: string;
  channel: ChannelType;
  product_policy?: string;
  journey_stage?: string;
  source?: string;
  complaint_detected?: boolean;
  vulnerability_detected?: boolean;
  initial_severity?: number;
  thread_messages: ThreadMessage[];
}

const SignalCreationForm: React.FC<SignalCreationFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onBack
}) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<SignalFormData>({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_id: '',
    verbatim: '',
    channel: 'chat',
    product_policy: '',
    journey_stage: '',
    source: '',
    complaint_detected: false,
    vulnerability_detected: false,
    initial_severity: 50,
    thread_messages: []
  });

  const [currentMessage, setCurrentMessage] = useState('');
  const [currentRole, setCurrentRole] = useState<'customer' | 'agent'>('customer');
  const [datasetId, setDatasetId] = useState<string | null>(null);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.customer_name.trim()) {
      newErrors.customer_name = 'Customer name is required';
    }

    if (!formData.verbatim.trim()) {
      newErrors.verbatim = 'Customer verbatim is required';
    }

    if (formData.customer_email && !/\S+@\S+\.\S+/.test(formData.customer_email)) {
      newErrors.customer_email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      // Prepare the signal data for the API
      const signalData: any = {
        raw_text: formData.verbatim,
        channel_type: formData.channel,
        customer_name: formData.customer_name || undefined,
        customer_id: formData.customer_id || undefined,
        customer_email: formData.customer_email || undefined,
        customer_phone: formData.customer_phone || undefined,
        occurred_at: new Date().toISOString(),
        journey_stage: formData.journey_stage || undefined,
        product_category: formData.product_policy || undefined,
        dataset_id: datasetId || undefined,
        channel_metadata: {
          source_type: formData.source || 'manual_entry',
          journey_stage: formData.journey_stage || undefined,
          created_via: 'signal_creation_hub',
          form_version: '3.0',
          initial_severity: formData.initial_severity,
          complaint_detected: formData.complaint_detected,
          vulnerability_detected: formData.vulnerability_detected
        },
        thread_messages: formData.thread_messages,
        source_id: formData.source || undefined,
        source_type: 'manual_entry'
      };

      const response = await ingestSignal(signalData);

      // Invalidate datasets query to refresh the feedback set table
      await queryClient.invalidateQueries({ queryKey: datasetKeys.all });

      onSubmit(response);

      // Reset form
      setFormData({
        customer_name: '',
        customer_email: '',
        customer_phone: '',
        customer_id: '',
        verbatim: '',
        channel: 'chat',
        product_policy: '',
        journey_stage: '',
        source: '',
        complaint_detected: false,
        vulnerability_detected: false,
        initial_severity: 50,
        thread_messages: []
      });
      setErrors({});
      
    } catch (err) {
      console.error('Error creating Record:', err);
      setErrors({
        submit: err instanceof Error ? err.message : 'Failed to create Record. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: keyof SignalFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const addThreadMessage = () => {
    if (!currentMessage.trim()) return;
    
    const newMessage: ThreadMessage = {
      author_type: currentRole,
      author_name: currentRole === 'customer' ? formData.customer_name || 'Customer' : 'Support Agent',
      content: currentMessage.trim(),
      sent_at: new Date().toISOString(),
      message_type: currentRole === 'customer' ? 'customer_message' : 'agent_response'
    };
    
    setFormData(prev => ({
      ...prev,
      thread_messages: [...prev.thread_messages, newMessage]
    }));
    
    setCurrentMessage('');
  };

  const removeThreadMessage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      thread_messages: prev.thread_messages.filter((_, i) => i !== index)
    }));
  };

  const channelOptions: { value: ChannelType; label: string }[] = [
    { value: 'chat', label: 'Chat' },
    { value: 'call_center', label: 'Call Center' },
    { value: 'email', label: 'Email' },
    { value: 'social_media', label: 'Social Media' },
    { value: 'survey', label: 'Survey' },
    { value: 'review', label: 'Review' }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -100 }}
      className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
      style={{ fontFamily: 'Open Sans, sans-serif' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <div className="flex items-center space-x-4">
          {onBack && (
            <button
              onClick={onBack}
              className="text-gray-500 hover:text-gray-700 transition-colors"
              disabled={isSubmitting}
            >
              <ArrowLeftIcon className="w-6 h-6" />
            </button>
          )}
          <div>
            <h2 className="text-xl font-bold text-gray-900">Create Record Manually</h2>
            <p className="text-sm text-gray-600 mt-1">
              Enter record details to create a new record
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 transition-colors"
          disabled={isSubmitting}
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Error message */}
        {errors.submit && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{errors.submit}</p>
              </div>
            </div>
          </div>
        )}

        {/* Dataset Selection */}
        <DatasetSelector
          value={datasetId}
          onChange={setDatasetId}
          autoSelectDefault={false}
        />

        {/* Customer Information Section */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer Name *
              </label>
              <input
                type="text"
                value={formData.customer_name}
                onChange={(e) => handleChange('customer_name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500 ${
                  errors.customer_name ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter customer name"
                disabled={isSubmitting}
              />
              {errors.customer_name && (
                <p className="text-red-500 text-xs mt-1">{errors.customer_name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer ID
              </label>
              <input
                type="text"
                value={formData.customer_id}
                onChange={(e) => handleChange('customer_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="CUST-#123456"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                value={formData.customer_email}
                onChange={(e) => handleChange('customer_email', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500 ${
                  errors.customer_email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="customer@email.com"
                disabled={isSubmitting}
              />
              {errors.customer_email && (
                <p className="text-red-500 text-xs mt-1">{errors.customer_email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone
              </label>
              <input
                type="tel"
                value={formData.customer_phone}
                onChange={(e) => handleChange('customer_phone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="+44 7800 123456"
                disabled={isSubmitting}
              />
            </div>
          </div>
        </div>

        {/* Record Information Section */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Record Information</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer Verbatim *
              </label>
              <textarea
                value={formData.verbatim}
                onChange={(e) => handleChange('verbatim', e.target.value)}
                rows={4}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500 ${
                  errors.verbatim ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter the customer's exact words or feedback..."
                disabled={isSubmitting}
              />
              {errors.verbatim && (
                <p className="text-red-500 text-xs mt-1">{errors.verbatim}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Channel *
                </label>
                <select
                  value={formData.channel}
                  onChange={(e) => handleChange('channel', e.target.value as ChannelType)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  disabled={isSubmitting}
                >
                  {channelOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Initial Severity (0-100)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.initial_severity}
                  onChange={(e) => handleChange('initial_severity', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  placeholder="50"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Business Context Section */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Business Context</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Product/Policy
              </label>
              <input
                type="text"
                value={formData.product_policy}
                onChange={(e) => handleChange('product_policy', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="Home Insurance Policy #8200"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Journey Stage
              </label>
              <input
                type="text"
                value={formData.journey_stage}
                onChange={(e) => handleChange('journey_stage', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="Claims → Payout"
                disabled={isSubmitting}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Source
              </label>
              <input
                type="text"
                value={formData.source}
                onChange={(e) => handleChange('source', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder="Support Ticket"
                disabled={isSubmitting}
              />
            </div>
          </div>
        </div>

        {/* Initial Assessments */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Initial Assessments</h3>
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="complaint_detected"
                checked={formData.complaint_detected}
                onChange={(e) => handleChange('complaint_detected', e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                disabled={isSubmitting}
              />
              <label htmlFor="complaint_detected" className="ml-2 text-sm text-gray-700">
                Potential complaint detected
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="vulnerability_detected"
                checked={formData.vulnerability_detected}
                onChange={(e) => handleChange('vulnerability_detected', e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                disabled={isSubmitting}
              />
              <label htmlFor="vulnerability_detected" className="ml-2 text-sm text-gray-700">
                Customer vulnerability detected
              </label>
            </div>
          </div>
        </div>

        {/* Conversation Thread Section */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Conversation Thread (Optional)</h3>
          <p className="text-sm text-gray-600 mb-4">
            Add messages to recreate the conversation between the customer and agent
          </p>
          
          {/* Add Message Controls */}
          <div className="space-y-3">
            <div className="flex gap-3">
              <select
                value={currentRole}
                onChange={(e) => setCurrentRole(e.target.value as 'customer' | 'agent')}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                disabled={isSubmitting}
              >
                <option value="customer">Customer</option>
                <option value="agent">Agent</option>
              </select>
              
              <input
                type="text"
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addThreadMessage();
                  }
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                placeholder={`Enter ${currentRole === 'customer' ? 'customer' : 'agent'} message...`}
                disabled={isSubmitting}
              />
              
              <button
                type="button"
                onClick={addThreadMessage}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                disabled={!currentMessage.trim() || isSubmitting}
              >
                Add Message
              </button>
            </div>
          </div>

          {/* Display Thread Messages */}
          {formData.thread_messages.length > 0 && (
            <div className="mt-4 space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-4">
              {formData.thread_messages.map((message, index) => (
                <div 
                  key={index} 
                  className={`flex items-start justify-between p-3 rounded-lg ${
                    message.author_type === 'customer' ? 'bg-blue-50' : 'bg-gray-50'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`text-xs font-semibold ${
                        message.author_type === 'customer' ? 'text-blue-700' : 'text-gray-700'
                      }`}>
                        {message.author_name || (message.author_type === 'customer' ? 'Customer' : 'Agent')}
                      </span>
                      <span className="text-xs text-gray-500">
                        {message.sent_at ? new Date(message.sent_at).toLocaleString() : ''}
                      </span>
                    </div>
                    <p className="text-sm text-gray-800">{message.content}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeThreadMessage(index)}
                    className="ml-2 text-red-500 hover:text-red-700"
                    disabled={isSubmitting}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </form>

      {/* Footer */}
      <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
        <div className="text-sm text-gray-600">
          * Required fields
        </div>
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !datasetId || !formData.customer_name.trim() || !formData.verbatim.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Creating Record...
              </>
            ) : (
              'Create Record'
            )}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default SignalCreationForm;